import {
  Avatar,
  Box,
  Dialog,
  DialogTitle,
  IconButton,
  LinearProgress,
  Typography,
  useTheme,
} from "@mui/material";
import { useState } from "react";
import CloseIcon from "@mui/icons-material/Close";
import { Document, Page, pdfjs } from "react-pdf";
import PropTypes from "prop-types";
import { fileUrl } from "./Utils";
import AccessibleCircularProgress from "./AccessibleCircularProgress";
import { useTranslation } from "react-i18next";

pdfjs.GlobalWorkerOptions.workerSrc = `//unpkg.com/pdfjs-dist@${pdfjs.version}/build/pdf.worker.min.js`;

const FileStatus = {
  PENDING: "pending",
  UPLOADING: "uploading",
  SUCCESS: "success",
  ERROR: "error",
};
function FilesRenderer(props) {
  const { setSelectedFiles, files, preview = false, tone } = props;
  // Opt-in only — every other consumer (GRM/PGR/CustomTimelineStepper) keeps
  // its existing chip look. `tone="scheme"` is the scheme-apply file-upload
  // chrome (Figma nodes 639-15616/639-15318): amber while uploading, green
  // once uploaded — the error state is untouched (no Figma frame for it).
  const isSchemeTone = tone === "scheme";

  const [openPreview, setOpenPreview] = useState(false);
  const [previewFile, setPreviewFile] = useState({});
  const [numPages, setNumPages] = useState(null);

  const { t } = useTranslation();
  const theme = useTheme();
  const isDarkTheme = theme.palette.mode === "dark";

  const closePreview = () => {
    setPreviewFile(null);
    setNumPages(null);
  };

  const getCardColors = (status) => {
    const defaultColors = {
      bg: isDarkTheme ? theme.palette.background.tertiaryGreen : theme.palette.background.default,
      border: "#D7DEDA",
    };
    if (!isSchemeTone) return defaultColors;
    switch (status) {
      case FileStatus.UPLOADING:
      case FileStatus.PENDING:
        return { bg: theme.palette.accent.butterscotchBg, border: theme.palette.accent.butterscotch };
      case FileStatus.SUCCESS:
        return { bg: theme.palette.primary.softSurface, border: theme.palette.primary.interactive };
      default:
        return defaultColors;
    }
  };

  const getStatusIcon = (status) => {
    if (isSchemeTone) {
      switch (status) {
        case FileStatus.SUCCESS:
          return "/assets/fileUploadSuccess.svg";
        case FileStatus.ERROR:
          return "/assets/agentOperationFail.svg";
        case FileStatus.UPLOADING:
        case FileStatus.PENDING:
        default:
          return "/assets/fileUploadPending.svg";
      }
    }
    switch (status) {
      case FileStatus.SUCCESS:
        return "/assets/documentUpload.svg";
      case FileStatus.ERROR:
        return "/assets/agentOperationFail.svg";
      case FileStatus.UPLOADING:
      case FileStatus.PENDING:
      default:
        return "/assets/documentUpload.svg";
    }
  };

  const getActionIcon = (status, index) => {
    if (isSchemeTone && status === FileStatus.SUCCESS) {
      return "/assets/fileUploadRemove.svg";
    }
    switch (status) {
      case FileStatus.SUCCESS:
        return "/assets/DeleteTrash.svg";
      default:
        return `/assets/${theme.palette.mode}/cross.svg`;
    }
  };

  const getProgress = (file) => {
    switch (file.status) {
      case FileStatus.UPLOADING:
        return `${t("FILE_UPLOADING")} ${file.progress}%`;
      case FileStatus.SUCCESS:
        // Assuming file.size is in bytes, we can format it to KB or MB if needed
        const fileSize = (file.originalFile?.size / (1024 * 1024)).toFixed(2); // Convert bytes to KB
        if (isNaN(fileSize)) return "";
        return `${t("FILE_UPLOADED")} ${fileSize} MB`;
      case FileStatus.ERROR:
        return t("FILE_UPLOAD_FAILED");
      default:
        return "";
    }
  };

  const getProgressTextColor = (status) => {
    if (status === FileStatus.ERROR) return theme.palette.text.error;
    if (isSchemeTone) {
      if (status === FileStatus.SUCCESS) return theme.palette.primary.main;
      if (status === FileStatus.UPLOADING || status === FileStatus.PENDING) {
        return theme.palette.accent.butterscotch;
      }
    }
    return theme.palette.text.textGrey;
  };

  const onViewClick = async (file, type) => {
    let fileType = type;
    let fileUrlToUse;

    if (file?.fileStoreId) {
      const url = fileUrl(file.fileStoreId);

      // If type is not provided, fetch the Content-Type header
      if (!fileType) {
        try {
          const response = await fetch(url, { method: "HEAD" });
          fileType = response.headers.get("Content-Type");
        } catch (error) {
          console.error("Failed to get file type from headers", error);
        }
      }

      fileUrlToUse = url;
    } else {
      const objectUrl = URL.createObjectURL(file);

      // If it's a File object (e.g., from input), try using its .type
      if (!fileType && file?.type) {
        fileType = file.type;
      }

      fileUrlToUse = objectUrl;
    }

    setPreviewFile({
      name: file?.name,
      type: fileType,
      url: fileUrlToUse,
    });

    setOpenPreview(true);
  };

  const removeFile = (index) => {
    const updatedFiles = files.filter((_, i) => i !== index);
    setSelectedFiles(updatedFiles);
  };

  return (
    <Box className="">
      <Box className="flex flex-wrap gap-4">
        {files?.map((file, index) => {
          if (preview && file.status === FileStatus.ERROR) return;

          const type = file?.documentType || file?.type;
          const name = file?.documentType || file?.name;
          const cardColors = getCardColors(file.status);
          // Figma 639-15317 only specs the SUCCESS chip for tone="scheme" —
          // fill the field's own column instead of the fixed 273px other
          // tones use, and drop to its 24/20px icon scale.
          const isSchemeSuccess = isSchemeTone && file.status === FileStatus.SUCCESS;

          return (
            <Box
              key={index + ""}
              className={
                isSchemeTone
                  ? "flex flex-col rounded-[10px] mb-4 w-full"
                  : "flex flex-col rounded-[4px] mb-4 w-full md:w-[calc(50%-8px)] lg:w-[273px]"
              }
              bgcolor={cardColors.bg}
              sx={{ border: "1px solid", borderColor: cardColors.border }}
            >
              <Box
                key={index + " "}
                className="flex w-full justify-between items-start p-2 gap-2"
              >
                <Box className="flex items-start gap-2">
                  <Avatar
                    src={`${window.contextPath}${getStatusIcon(file.status)}`}
                    alt={t(file.status)}
                    className={isSchemeTone ? "!w-[24px] !h-[24px]" : "!w-[32px] !h-[32px]"}
                  />
                  <Box className="flex flex-col justify-center items-start gap-1">
                    <Typography
                      className="!text-sm !font-semibold !leading-5"
                      color={theme.palette.text.primary}
                    >
                      {name?.length > 14 ? name?.slice(0, 14) + "..." : name}
                    </Typography>
                    <span
                      class="font-normal text-xs leading-[120%] text-[#5C6460]"
                      style={{ color: getProgressTextColor(file.status) }}
                    >
                      {getProgress(file)}
                    </span>
                  </Box>
                </Box>
                <Box className="flex min-w-6">
                  {file.status === FileStatus.SUCCESS && (
                    <IconButton
                      className="!p-1"
                      onClick={() => onViewClick(file, type)}
                      aria-label={t('preview')}
                    >
                      <img
                        src={`${window.contextPath}/assets/${theme.palette.mode}/preview.svg`}
                        alt={t('preview')}
                        className={isSchemeTone ? "w-5 h-5" : "w-4 h-4"}
                      />
                    </IconButton>
                  )}
                  {!preview && (
                    <IconButton
                      className="!p-1"
                      onClick={() => removeFile(index)}
                      aria-label={t('remove')}
                    >
                      <img
                        src={`${window.contextPath}${getActionIcon(
                          file.status,
                          index
                        )}`}
                        alt={t('remove')}
                        className={isSchemeTone ? "w-5 h-5" : "w-4 h-4"}
                      />
                    </IconButton>
                  )}
                </Box>
              </Box>
              {!preview && !isSchemeSuccess && (
                <LinearProgress
                  className="w-full !h-2"
                  variant="determinate"
                  value={file.progress}
                  sx={
                    isSchemeTone
                      ? {
                          backgroundColor: theme.palette.neutral.stroke,
                          "& .MuiLinearProgress-bar": {
                            backgroundColor: theme.palette.primary.main,
                          },
                        }
                      : {
                          // Set the background to white
                          backgroundColor: isDarkTheme ? "#FFFFFF" : "#D7DEDA",
                          // Apply gradient to the progress bar (the filled portion)
                          "& .MuiLinearProgress-bar": {
                            background: isDarkTheme
                              ? "linear-gradient(90deg, rgba(115, 232, 148, 0.40) 0%, rgba(26, 92, 75, 0.20) 100%), #85BC31;"
                              : "linear-gradient(90deg, rgba(115, 232, 148, 0.4) 0%, rgba(26, 92, 75, 0.2) 100%), linear-gradient(0deg, #18342D, #18342D), #1A5C4B",
                          },
                        }
                  }
                />
              )}
            </Box>
          );
        })}
      </Box>

      <Dialog
        open={openPreview}
        onClose={() => setOpenPreview(false)}
        maxWidth="lg"
      >
        <DialogTitle className="flex items-center justify-between">
          <Typography>{previewFile?.name}</Typography>
          <IconButton onClick={() => setOpenPreview(false)} size="small" aria-label={t('close')}>
            <CloseIcon />
          </IconButton>
        </DialogTitle>

        <Box className="p-4">
          {previewFile?.type === "application/pdf" ? (
            <Document
              file={previewFile.url}
              onLoadSuccess={({ numPages }) => setNumPages(numPages)}
              loading={
                <AccessibleCircularProgress color="success" data-testid="pdf-document" />
              }
            >
              {Array.from(new Array(numPages)).map((_, index) => (
                <Page
                  key={`page_${index}`}
                  pageNumber={index + 1}
                  renderAnnotationLayer={false}
                  renderTextLayer={false}
                  width={800}
                />
              ))}
            </Document>
          ) : (
            <img
              src={previewFile?.url}
              alt={t('preview')}
              className="max-h-[90vh] w-auto mx-auto"
            />
          )}
        </Box>
      </Dialog>
    </Box>
  );
}

FilesRenderer.propTypes = {
  preview: PropTypes.bool,
  selectedfilesFiles: PropTypes.array,
  setSelectedFiles: PropTypes.func,
  tone: PropTypes.oneOf(["scheme"]),
};

export default FilesRenderer;
